import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Activity,
  AlertTriangle,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  Telescope,
  Workflow,
  Radio,
  Bell,
  Ticket,
  FolderKanban,
  PieChart,
  Users,
  Sparkles,
  Globe,
} from 'lucide-react'
import { cn } from '../../lib/utils'

interface NavItem {
  path: string
  label: string
  icon: React.ElementType
  badge?: number
}

interface NavSection {
  title: string
  items: NavItem[]
}

const navigation: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'Automations',
    items: [
      { path: '/automations', label: 'All Automations', icon: Workflow },
    ],
  },
  {
    title: 'Monitoring',
    items: [
      { path: '/traces', label: 'All Runs', icon: Activity },
      { path: '/errors', label: 'Errors', icon: AlertTriangle },
      { path: '/activity', label: 'Activity Log', icon: Radio },
    ],
  },
  {
    title: 'Incident Management',
    items: [
      { path: '/alerts', label: 'Alerts', icon: Bell },
      { path: '/tickets', label: 'Tickets', icon: Ticket },
    ],
  },
  {
    title: 'Testing',
    items: [
      { path: '/test-cases', label: 'Test Cases', icon: FlaskConical },
      { path: '/prompt-tests', label: 'Prompt Tests', icon: Sparkles },
      { path: '/test-suites', label: 'Test Suites', icon: FolderKanban },
      { path: '/coverage', label: 'Coverage', icon: PieChart },
    ],
  },
  {
    title: 'Settings',
    items: [
      { path: '/services', label: 'Services', icon: Globe },
      { path: '/users', label: 'Users', icon: Users },
    ],
  },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation()

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-slate-900 transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-slate-800 px-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
              <Telescope className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <span className="text-lg font-semibold text-white">Observatory</span>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {navigation.map((section) => (
            <div key={section.title} className="mb-4">
              {!collapsed && (
                <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {section.title}
                </h3>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.path)
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={cn(
                          'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          active
                            ? 'bg-slate-800 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white',
                          collapsed && 'justify-center px-2'
                        )}
                        title={collapsed ? item.label : undefined}
                      >
                        <Icon className={cn('h-5 w-5 flex-shrink-0', active && 'text-blue-400')} />
                        {!collapsed && (
                          <>
                            <span className="flex-1">{item.label}</span>
                            {item.badge !== undefined && (
                              <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium text-white">
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <div className="border-t border-slate-800 p-2">
          <button
            onClick={onToggle}
            className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </div>
    </aside>
  )
}
